import { Component, inject, signal } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { WHATS_NEW_ITEMS, WHATS_NEW_MAX_ITEMS } from './whats-new-items';

/**
 * "Novidades" dialog — recent FEATURE releases, maintained by hand in
 * whats-new-items.ts (+ i18n keys). Accordion list: at most one item open,
 * the first starts open. Opened from the orange topbar pill.
 */
@Component({
  selector: 'app-whats-new-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, TranslateModule],
  template: `
    <div class="wn-dialog">
      <div class="wn-head">
        <span class="wn-tag">{{ 'WHATS_NEW.TAG' | translate }}</span>
        <h2 class="wn-title">DreamBooksPro Express</h2>
        <button type="button" class="wn-close" (click)="close()" [attr.aria-label]="'WHATS_NEW.CLOSE' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="wn-intro">
        <p>{{ 'WHATS_NEW.INTRO' | translate }}</p>
      </div>

      <div class="wn-list">
        @for (item of items; track item.id; let i = $index) {
          <div class="wn-item" [class.is-open]="openIndex() === i">
            <button type="button" class="wn-item-head"
                    [attr.aria-expanded]="openIndex() === i"
                    (click)="toggle(i)">
              <span class="wn-cat">{{ ('WHATS_NEW.CATS.' + item.cat) | translate }}</span>
              <span class="wn-item-title">{{ ('WHATS_NEW.ITEMS.' + item.id + '.TITLE') | translate }}</span>
              <mat-icon class="wn-chev">chevron_right</mat-icon>
            </button>
            <div class="wn-item-body">
              <div>
                <p>{{ ('WHATS_NEW.ITEMS.' + item.id + '.BODY') | translate }}</p>
                @if (item.twoParagraphs) {
                  <p>{{ ('WHATS_NEW.ITEMS.' + item.id + '.BODY2') | translate }}</p>
                }
                <span class="wn-meta">{{ ('WHATS_NEW.ITEMS.' + item.id + '.META') | translate }}</span>
              </div>
            </div>
          </div>
        }
      </div>

      <div class="wn-foot">
        <span class="wn-count">{{ 'WHATS_NEW.COUNT' | translate:{ count: countLabel } }}</span>
        <button type="button" class="wn-done" (click)="close()">{{ 'WHATS_NEW.CLOSE' | translate }}</button>
      </div>
    </div>
  `,
  styles: [`
    /* The component owns its size (the MatDialog config passes none): short
       monitors get a fitting height with the list scrolling inside, and the
       native resize grip (bottom-right) lets the user enlarge the panel. */
    .wn-dialog {
      display: flex;
      flex-direction: column;
      width: min(560px, 95vw);
      height: min(620px, 85vh);
      min-width: min(420px, 92vw);
      min-height: 320px;
      max-width: 95vw;
      max-height: 92vh;
      resize: both;
      overflow: hidden;
      background: #fff;
      color: #1e2320;
      font-family: inherit;
    }

    .wn-head {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 16px 16px 14px 20px;
      border-bottom: 1px solid #ece8e1;
      flex-shrink: 0;
    }

    .wn-tag {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #ef6031;
      background: #fdede7;
      padding: 4px 8px;
      border-radius: 6px;
      line-height: 1;
    }

    .wn-title {
      margin: 0;
      font-size: 15px;
      font-weight: 500;
      letter-spacing: -0.01em;
    }

    .wn-close {
      margin-left: auto;
      width: 32px;
      height: 32px;
      display: grid;
      place-items: center;
      background: transparent;
      border: 0;
      border-radius: 8px;
      color: #6e7370;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;

      mat-icon { font-size: 18px; width: 18px; height: 18px; }
      &:hover { background: #f5f2ec; color: #1e2320; }
    }

    .wn-intro {
      padding: 18px 24px 4px;
      flex-shrink: 0;

      p { margin: 0; font-size: 13.5px; line-height: 1.55; color: #6e7370; }
    }

    .wn-list {
      padding: 12px 12px 16px;
      overflow-y: auto;
      min-height: 0;
    }

    .wn-item {
      border-radius: 12px;
      transition: background 0.18s;
    }
    .wn-item + .wn-item { box-shadow: inset 0 1px 0 #ece8e1; }
    .wn-item.is-open { background: #fbfaf7; box-shadow: none; }
    .wn-item.is-open + .wn-item { box-shadow: none; }

    .wn-item-head {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 14px 14px 14px 12px;
      background: transparent;
      border: 0;
      border-radius: 12px;
      font: inherit;
      text-align: left;
      color: #1e2320;
      cursor: pointer;
    }

    .wn-cat {
      flex: 0 0 62px;
      font-family: ui-monospace, 'Cascadia Mono', 'Segoe UI Mono', Menlo, monospace;
      font-size: 10.5px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #3d7056;
    }

    .wn-item-title {
      flex: 1;
      font-size: 14.5px;
      font-weight: 500;
      letter-spacing: -0.01em;
    }

    .wn-chev {
      flex: 0 0 auto;
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: #6e7370;
      transition: transform 0.22s ease, color 0.18s;
    }
    .wn-item.is-open .wn-chev { transform: rotate(90deg); color: #1e2320; }

    .wn-item-body {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 0.24s ease;
    }
    .wn-item.is-open .wn-item-body { grid-template-rows: 1fr; }
    .wn-item-body > div { overflow: hidden; }

    .wn-item-body p {
      margin: 0;
      padding: 0 16px 14px 88px;
      font-size: 13.5px;
      line-height: 1.6;
      color: #6e7370;
      max-width: 56ch;
    }

    .wn-meta {
      display: inline-block;
      margin: 0 0 0 88px;
      padding: 0 0 16px;
      font-family: ui-monospace, 'Cascadia Mono', 'Segoe UI Mono', Menlo, monospace;
      font-size: 11px;
      color: #9a9a9a;
    }

    .wn-foot {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 14px 20px 16px 24px;
      border-top: 1px solid #ece8e1;
      flex-shrink: 0;
    }

    .wn-count {
      font-family: ui-monospace, 'Cascadia Mono', 'Segoe UI Mono', Menlo, monospace;
      font-size: 11px;
      color: #9a9a9a;
    }

    .wn-done {
      font: inherit;
      font-size: 13.5px;
      font-weight: 500;
      border-radius: 8px;
      padding: 9px 18px;
      cursor: pointer;
      margin-left: auto;
      background: #3d7056;
      border: 1px solid #3d7056;
      color: #fff;
      transition: background 0.15s, transform 0.1s;

      &:hover { background: #2c5842; }
      &:active { transform: translateY(1px); }
    }

    @media (max-width: 520px) {
      .wn-dialog { resize: vertical; }
      .wn-intro { padding: 16px 16px 4px; }
      .wn-foot { padding: 12px 16px 14px; }
      .wn-item-head { gap: 0; flex-wrap: wrap; padding: 12px; }
      .wn-cat { flex: 0 0 100%; margin-bottom: 4px; }
      .wn-item-title { flex: 1 1 auto; }
      .wn-item-body p { padding-left: 12px; }
      .wn-meta { margin-left: 12px; padding-left: 0; }
    }
  `]
})
export class WhatsNewDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<WhatsNewDialogComponent>);

  /** Newest first, hard-capped at WHATS_NEW_MAX_ITEMS; the list scrolls. */
  readonly items = WHATS_NEW_ITEMS.slice(0, WHATS_NEW_MAX_ITEMS);
  /** Index of the single open item (-1 = all collapsed). First starts open. */
  readonly openIndex = signal(0);
  readonly countLabel = String(this.items.length).padStart(2, '0');

  toggle(index: number): void {
    this.openIndex.set(this.openIndex() === index ? -1 : index);
  }

  close(): void {
    this.dialogRef.close();
  }
}
